import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { baseurImage } from "../../Baseurl";
import axiosInstance from "../../api/axiosInstance";
import Swal from "sweetalert2";

const COLORS = {
  gray:  '#5B6B79',
  blue:  '#4680ff',
  green: '#2CA87F',
  red:   '#9E2B25',
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 12, color: '#2d3748', padding: '7px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

export default function ViewCompany() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const company = state?.company;

  const [priceListOpen, setPriceListOpen] = useState(false);
  const [priceTab, setPriceTab] = useState('services');
  const [services, setServices] = useState([]);
  const [companyPrices, setCompanyPrices] = useState({});
  const [priceListLoading, setPriceListLoading] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceSearch, setPriceSearch] = useState("");

  const [labServices, setLabServices] = useState([]);
  const [companyLabPrices, setCompanyLabPrices] = useState({});
  const [labPricesLoading, setLabPricesLoading] = useState(false);
  const [labSearch, setLabSearch] = useState("");

  const openPriceList = async () => {
    setPriceListOpen(true);
    setPriceTab('services');
    setPriceListLoading(true);
    setLabPricesLoading(true);
    try {
      const [svcRes, labRes] = await Promise.all([
        axiosInstance.get(`getCompanyServicePrices/${company.id}`),
        axiosInstance.get(`getCompanyLabPrices/${company.id}`),
      ]);
      const svcRows = svcRes.data?.data || [];
      setServices(svcRows);
      const svcInitial = {};
      svcRows.forEach(s => { svcInitial[s.service_id] = s.company_price != null ? String(s.company_price) : ""; });
      setCompanyPrices(svcInitial);

      const labRows = labRes.data?.data || [];
      setLabServices(labRows);
      const labInitial = {};
      labRows.forEach(l => { labInitial[l.lab_service_id] = l.company_price != null ? String(l.company_price) : ""; });
      setCompanyLabPrices(labInitial);
    } catch (e) {
      Swal.fire("Error", e.response?.data?.message || e.message || "Failed to load price list", "error");
      setPriceListOpen(false);
    } finally {
      setPriceListLoading(false);
      setLabPricesLoading(false);
    }
  };

  const savePriceList = async () => {
    setSavingPrices(true);
    try {
      if (priceTab === 'services') {
        const prices = services.map(s => ({
          service_id: s.service_id,
          price: companyPrices[s.service_id] !== "" ? companyPrices[s.service_id] : null,
        }));
        await axiosInstance.post(`saveCompanyServicePrices`, { company_id: company.id, prices });
      } else {
        const prices = labServices.map(l => ({
          lab_service_id: l.lab_service_id,
          price: companyLabPrices[l.lab_service_id] !== "" ? companyLabPrices[l.lab_service_id] : null,
        }));
        await axiosInstance.post(`saveCompanyLabPrices`, { company_id: company.id, prices });
      }
      Swal.fire("Success", "Price list saved successfully", "success");
      setPriceListOpen(false);
    } catch (e) {
      Swal.fire("Error", e.response?.data?.message || "Failed to save price list", "error");
    } finally {
      setSavingPrices(false);
    }
  };

  const filteredServices = services.filter(s => {
    const q = priceSearch.toLowerCase();
    if (!q) return true;
    return (
      (s.serviceCode && s.serviceCode.toLowerCase().includes(q)) ||
      (s.serviceName && s.serviceName.toLowerCase().includes(q)) ||
      (s.category && s.category.toLowerCase().includes(q))
    );
  });

  const filteredLabServices = labServices.filter(l => {
    const q = labSearch.toLowerCase();
    if (!q) return true;
    return (
      (l.serviceName && l.serviceName.toLowerCase().includes(q)) ||
      (l.category && l.category.toLowerCase().includes(q))
    );
  });

  const statusMeta = (status) => {
    if (!status || status.toLowerCase() === 'active') return { bg: '#dcfce7', color: '#166534' };
    if (status.toLowerCase() === 'inactive') return { bg: '#fee2e2', color: '#991b1b' };
    return { bg: '#e8ecf0', color: COLORS.gray };
  };

  const details = company ? [
    { icon: 'ti ti-phone',    label: 'Phone',        value: company.telephone },
    { icon: 'ti ti-map-pin',  label: 'Address',      value: company.address },
    { icon: 'ti ti-mail',     label: 'Email',        value: company.email },
    { icon: 'ti ti-world',    label: 'Website',      value: company.website, isLink: true },
    { icon: 'ti ti-calendar', label: 'Created',      value: company.created_at ? new Date(company.created_at).toLocaleDateString('en-GB') : '—' },
    { icon: 'ti ti-refresh',  label: 'Last Updated', value: company.updated_at ? new Date(company.updated_at).toLocaleDateString('en-GB') : '—' },
  ] : [];

  const sm = statusMeta(company?.status);

  return (
    <>
    <div className="pc-container">
      <div className="pc-content">

        {!company ? (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', padding: 24, color: COLORS.gray, fontSize: 13 }}>
            No company data for ID {id}. Open from Insurance → Companies tab.
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

            {/* ── Header ── */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                  background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
              >
                <i className="ti ti-arrow-left" />
              </button>

              {/* Logo */}
              <div style={{
                width: 42, height: 42, borderRadius: 10, border: '1px solid #e8ecf0',
                background: '#f8f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, overflow: 'hidden',
              }}>
                {company.logo
                  ? <img src={`${baseurImage}${company.logo}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <i className="ti ti-building-bank" style={{ color: COLORS.blue, fontSize: 20 }} />
                }
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>{company.companyName || '—'}</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Insurance Company</div>
              </div>

              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                background: sm.bg, color: sm.color,
              }}>
                {(company.status || 'Active').charAt(0).toUpperCase() + (company.status || 'Active').slice(1)}
              </span>
            </div>

            {/* ── Details Grid ── */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0',
                borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden',
              }}>
                {details.map(({ icon, label, value, isLink }, i) => (
                  <div key={i} style={{
                    padding: '12px 16px',
                    borderRight: (i + 1) % 3 !== 0 ? '1px solid #e8ecf0' : 'none',
                    borderBottom: i < 3 ? '1px solid #e8ecf0' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <i className={icon} style={{ color: COLORS.blue, fontSize: 13 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {label}
                      </span>
                    </div>
                    {isLink && value
                      ? <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 13, fontWeight: 500, color: COLORS.blue, wordBreak: 'break-all' }}>{value}</a>
                      : <span style={{ fontSize: 13, fontWeight: 600, color: '#1a202c' }}>{value || '—'}</span>
                    }
                  </div>
                ))}
              </div>
            </div>

            {/* ── Actions ── */}
            <div style={{ padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={openPriceList}
                style={{
                  height: 34, padding: '0 16px', borderRadius: 8, border: '1px solid #dde3eb',
                  background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.blue; e.currentTarget.style.color = COLORS.blue }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#dde3eb'; e.currentTarget.style.color = COLORS.gray }}
              >
                <i className="ti ti-list-details" style={{ fontSize: 15 }} /> Price List
              </button>
              <button
                className="bgBtn"
                style={{ height: 34, padding: '0 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => navigate(`/Admin/CompanyClaims/${company.id}`, { state: { company } })}
              >
                <i className="ti ti-file-invoice" style={{ fontSize: 15 }} /> View Claims
              </button>
            </div>

          </div>
        )}

      </div>
    </div>

    {/* ── Price List Modal ── */}
    {priceListOpen && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={() => setPriceListOpen(false)} />
        <div style={{
          position: 'relative', background: '#fff', borderRadius: 14, width: '92vw', maxWidth: 980,
          maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1,
        }}>

          {/* Modal Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-list-details" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Price List</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>{company?.companyName}</div>
            </div>
            <button
              onClick={() => setPriceListOpen(false)}
              style={{ marginLeft: 'auto', width: 28, height: 28, borderRadius: 7, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
            >
              <i className="ti ti-x" />
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e8ecf0', background: '#fafbfc', flexShrink: 0 }}>
            {[
              { key: 'services', label: 'Service Prices', icon: 'ti ti-list-details' },
              { key: 'labs',     label: 'Lab Prices',     icon: 'ti ti-flask' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setPriceTab(tab.key)}
                style={{
                  height: 38, padding: '0 18px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
                  color: priceTab === tab.key ? COLORS.blue : COLORS.gray,
                  borderBottom: priceTab === tab.key ? `2px solid ${COLORS.blue}` : '2px solid transparent',
                }}
              >
                <i className={tab.icon} style={{ fontSize: 14 }} /> {tab.label}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc', flexShrink: 0 }}>
            <div style={{ position: 'relative', width: 280 }}>
              <i className="ti ti-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: COLORS.gray, fontSize: 13, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder={priceTab === 'services' ? "Search service…" : "Search lab…"}
                value={priceTab === 'services' ? priceSearch : labSearch}
                onChange={e => priceTab === 'services' ? setPriceSearch(e.target.value) : setLabSearch(e.target.value)}
                style={{
                  width: '100%', height: 32, borderRadius: 7, border: '1px solid #dde3eb',
                  paddingLeft: 28, paddingRight: 10, fontSize: 12, outline: 'none', background: '#fff',
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {priceTab === 'services' ? (
              priceListLoading ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.gray }}>
                  <div className="spinner-border" style={{ color: COLORS.blue, width: 28, height: 28, borderWidth: 3 }} role="status" />
                  <div style={{ marginTop: 10, fontSize: 13 }}>Loading services…</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={thStyle}>Code</th>
                      <th style={thStyle}>Service Name</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>VAT %</th>
                      <th style={thStyle}>Std Cost</th>
                      <th style={thStyle}>Ins. Cost</th>
                      <th style={{ ...thStyle, minWidth: 130 }}>Company Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredServices.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                          <i className="ti ti-database-off" style={{ fontSize: 26, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                          No services found
                        </td>
                      </tr>
                    ) : filteredServices.map((s, i) => {
                      const rowBg = i % 2 === 0 ? '#fff' : '#fafbfc';
                      return (
                        <tr key={s.service_id} style={{ background: rowBg }}>
                          <td style={tdStyle}>
                            <code style={{ fontSize: 11, color: '#555', background: '#f4f4f8', padding: '1px 5px', borderRadius: 4 }}>
                              {s.serviceCode || '—'}
                            </code>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{s.serviceName}</td>
                          <td style={{ ...tdStyle, color: COLORS.gray }}>{s.category || '—'}</td>
                          <td style={tdStyle}>{s.vat_value != null ? `${s.vat_value}%` : '—'}</td>
                          <td style={tdStyle}>{s.standardCost != null ? Number(s.standardCost).toFixed(3) : '—'}</td>
                          <td style={tdStyle}>{s.insuranceCost != null ? Number(s.insuranceCost).toFixed(3) : '—'}</td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              placeholder="—"
                              value={companyPrices[s.service_id] ?? ""}
                              onChange={e => setCompanyPrices(prev => ({ ...prev, [s.service_id]: e.target.value }))}
                              style={{
                                width: '100%', height: 30, borderRadius: 6, border: '1px solid #dde3eb',
                                padding: '0 8px', fontSize: 12, outline: 'none', background: '#fff',
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : (
              labPricesLoading ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.gray }}>
                  <div className="spinner-border" style={{ color: COLORS.blue, width: 28, height: 28, borderWidth: 3 }} role="status" />
                  <div style={{ marginTop: 10, fontSize: 13 }}>Loading lab services…</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ ...thStyle, width: '45%' }}>Lab Service Name</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Std Price</th>
                      <th style={{ ...thStyle, minWidth: 130 }}>Company Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLabServices.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                          <i className="ti ti-database-off" style={{ fontSize: 26, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                          No lab services found
                        </td>
                      </tr>
                    ) : filteredLabServices.map((l, i) => {
                      const rowBg = i % 2 === 0 ? '#fff' : '#fafbfc';
                      return (
                        <tr key={l.lab_service_id} style={{ background: rowBg }}>
                          <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>{l.serviceName}</td>
                          <td style={{ ...tdStyle, color: COLORS.gray }}>{l.category || '—'}</td>
                          <td style={tdStyle}>{l.standard_price != null ? Number(l.standard_price).toFixed(3) : '—'}</td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              placeholder="—"
                              value={companyLabPrices[l.lab_service_id] ?? ""}
                              onChange={e => setCompanyLabPrices(prev => ({ ...prev, [l.lab_service_id]: e.target.value }))}
                              style={{
                                width: '100%', height: 30, borderRadius: 6, border: '1px solid #dde3eb',
                                padding: '0 8px', fontSize: 12, outline: 'none', background: '#fff',
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            )}
          </div>

          {/* Modal Footer */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e8ecf0', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => setPriceListOpen(false)}
              style={{ height: 34, padding: '0 16px', borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', fontSize: 13 }}
            >
              Cancel
            </button>
            <button
              className="bgBtn"
              style={{ height: 34, padding: '0 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              disabled={savingPrices || priceListLoading}
              onClick={savePriceList}
            >
              <i className="ti ti-device-floppy" style={{ fontSize: 15 }} />
              {savingPrices ? 'Saving…' : 'Save Price List'}
            </button>
          </div>

        </div>
      </div>
    )}
    </>
  );
}